import {
  AHSeparator,
  AHText,
  AHTextFonts,
  Box,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { PropsWithChildren, useMemo, useState } from "react";
import { Dimensions, Platform, StyleSheet, View } from "react-native";

import * as Haptics from "expo-haptics";
import { DateTime } from "luxon";

import * as d from "d3-scale";
import * as dtime from "d3-time";

import Svg, {
  ClipPath,
  G,
  Line,
  Rect,
  RectProps,
  Text,
} from "react-native-svg";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  runOnJS,
  scrollTo,
  SharedValue,
  useAnimatedProps,
  useAnimatedReaction,
  useAnimatedRef,
  useSharedValue,
} from "react-native-reanimated";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";

import { graphql, useFragment } from "react-relay";

import { KPIStoreAnalytics_KPI$key } from "./__generated__/KPIStoreAnalytics_KPI.graphql";
import { formatValue } from "arkh/common";
import { NumberTaskUnit } from "./__generated__/KPIPerWeekdayAnalytics.graphql";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { FormattedMessage } from "react-intl";
import { toClipPathUrl } from "./toClipPathUrl";

export function KPIStoreAnalytics(props: {
  kpi: KPIStoreAnalytics_KPI$key;
  sectionIdsFilter?: string[];
}) {
  const data = useFragment(
    graphql`
      fragment KPIStoreAnalytics_KPI on KPI {
        id
        title
        unit
        sectionAggregates {
          section {
            id
            title
            assignedTeam {
              icon
              color
              name
            }
          }
          results {
            date
            value
          }
        }
      }
    `,
    props.kpi
  );

  return (
    <Box spaceVertical="medium" spaceHorizontal="medium">
      <Box color="surface" cornerRadius="medium" spaceVertical="medium">
        <YearlyCountStats
          unit={data.unit}
          sections={(props.sectionIdsFilter
            ? data.sectionAggregates.filter(
                (s) =>
                  s?.section.id &&
                  props.sectionIdsFilter!.includes(s.section.id)
              )
            : data.sectionAggregates
          ).map((s, i) => ({
            id: s?.section.id ?? i + "",
            color: s?.section.assignedTeam?.color ?? "#000",
            title: (s?.section.title || s?.section.assignedTeam?.name) ?? "",
            results:
              s?.results.map((r) => ({
                date: DateTime.fromISO(r.date),
                value: r.value,
              })) ?? [],
          }))}
        />
      </Box>
    </Box>
  );
}

const AnimatedReact = Animated.createAnimatedComponent(Rect);

function Bar(
  props: RectProps & { activeIndex: SharedValue<number>; index: number }
) {
  const animatedProps = useAnimatedProps(() => {
    return {
      opacity: props.activeIndex.value === props.index ? 1 : 0.5,
    };
  });

  return <AnimatedReact {...props} animatedProps={animatedProps} />;
}

function HighlightBar(props: {
  barX: number[];
  activeIndex: SharedValue<number>;
  innerHeight: number;
}) {
  const theme = useTheme();
  const animatedProps = useAnimatedProps(() => {
    return {
      x: props.barX[props.activeIndex.value] - 7,
    };
  });

  return (
    <AnimatedReact
      {...props}
      width={14}
      opacity={0.05}
      fill={theme.darkerText}
      height={props.innerHeight}
      animatedProps={animatedProps}
    />
  );
}
export function YearlyCountStats({
  sections,
  unit,
}: {
  unit: NumberTaskUnit;
  sections: readonly {
    id: string;
    title: string;
    color: string;

    results: { readonly date: DateTime; readonly value: number }[];
  }[];
}) {
  const ul = useLayoutContext();
  const [width, setWidth] = useState(
    Dimensions.get("window").width - ul.space.medium * 2 - ul.space.xsmall * 2
  );

  const height = 280;

  const wide = useSizeClass().width === SizeClass.REGULAR;

  const endDate = DateTime.local().plus({ week: 1 }).startOf("week").toJSDate();
  const startDate = DateTime.local()
    .minus({ months: wide ? 12 : 6 })
    .endOf("week")
    .toJSDate();
  const paddingHorizxontal = ul.space.medium;
  const scaleX = useMemo(
    () =>
      d.scaleTime(
        [startDate, endDate],
        [paddingHorizxontal * 2, width - paddingHorizxontal]
      ),
    [endDate, paddingHorizxontal, startDate, width]
  );

  const historyData = dtime.timeWeeks(startDate, endDate).map((d) => {
    const timeStep = DateTime.fromJSDate(d).startOf("week");

    const results = sections
      .flatMap((s) => ({
        id: s.id,
        color: s.color,
        title: s.title,
        val: s.results.find((r) => r.date.equals(timeStep))?.value ?? 0,
      }))
      .sort((a, b) => a.val - b.val);
    return {
      date: d,
      results,
      total: results.reduce((a, b) => a + b.val, 0),
    };
  });

  const max = Math.max(...historyData.map((l) => l.total + 1));
  const [format, _u] = formatValue(unit, max);
  const u = _u ?? "";
  const paddingVertical = 6;

  const innerHeight = height - paddingVertical * 2;
  const scaleY = d
    .scaleLinear()
    .domain([max, 0])
    .range([height - paddingVertical * 4, 0])
    .nice(5);

  const barWidth = 6;

  const barX = useMemo(
    () => historyData.map((h) => scaleX(h.date) + barWidth / 2),
    [historyData, scaleX]
  );

  const activeIndex = useSharedValue(historyData.length - 1);

  const panGesture = Gesture.Pan()
    .onChange((d) => {
      const diffs = barX.map((h) => Math.abs(h - d.x));
      const min = Math.min(...diffs);
      const index = diffs.indexOf(min);

      if (index !== -1) {
        if (activeIndex.value !== index) {
          activeIndex.value = index;
          if (Platform.OS === "ios") {
            runOnJS(Haptics.impactAsync)(Haptics.ImpactFeedbackStyle.Medium);
          }
        }
      }
    })
    .activeOffsetX([-20, 20]);

  const tapGesture = Gesture.Tap().onStart((d) => {
    const diffs = barX.map((h) => Math.abs(h - d.x));
    const min = Math.min(...diffs);
    const index = diffs.indexOf(min);

    if (index !== -1) {
      if (activeIndex.value !== index) {
        activeIndex.value = index;
        if (Platform.OS === "ios") {
          runOnJS(Haptics.impactAsync)(Haptics.ImpactFeedbackStyle.Medium);
        }
      }
    }
  });
  const gesture = Gesture.Simultaneous(tapGesture, panGesture);
  const theme = useTheme();

  return (
    <Box>
      <Header
        activeIndex={activeIndex}
        history={historyData}
        format={(num) => format(num) + u}
      />
      <GestureDetector gesture={gesture}>
        <Animated.View
          style={{ flex: 1, marginTop: 20 }}
          onLayout={(e) => {
            setWidth(e.nativeEvent.layout.width);
          }}
        >
          <Svg
            style={{ flex: 1 }}
            pointerEvents="none"
            width={width}
            height={height}
          >
            <HighlightBar
              innerHeight={innerHeight - paddingVertical}
              activeIndex={activeIndex}
              barX={barX}
            />
            {scaleX.ticks(dtime.timeMonth.every(1)!).map((tick, i) => {
              return (
                <Line
                  key={tick.toISOString()}
                  y1={innerHeight - paddingVertical}
                  y2={0}
                  x1={scaleX(tick)}
                  strokeWidth={StyleSheet.hairlineWidth}
                  stroke="#bbb"
                  x2={scaleX(tick)}
                />
              );
            })}

            {historyData.map(({ total, date, results }, i) => {
              const y = Math.max(1, scaleY(total));
              let start = 0;
              return (
                <React.Fragment key={date.toISOString()}>
                  <ClipPath id={date.toISOString()}>
                    <Bar
                      width={barWidth}
                      height={y}
                      rx={2}
                      activeIndex={activeIndex}
                      index={i}
                      y={innerHeight - y - paddingVertical - start}
                      x={scaleX(date)}
                    />
                  </ClipPath>
                  <G clipPath={toClipPathUrl(date.toISOString())}>
                    {results.map((r, ii) => {
                      const y = Math.max(1, scaleY(r.val));
                      if (total === 0) {
                        return null;
                      }

                      const res = (
                        <Bar
                          fill={r.color}
                          key={date.toISOString() + ii}
                          width={barWidth}
                          height={y}
                          activeIndex={activeIndex}
                          index={i}
                          y={innerHeight - y - paddingVertical - start}
                          x={scaleX(date)}
                        />
                      );

                      start += y;
                      return res;
                    })}
                  </G>
                </React.Fragment>
              );
            })}

            {scaleX.ticks(dtime.timeMonth.every(1)!).map((tick, i) => {
              return (
                <Text
                  key={tick.toISOString()}
                  y={innerHeight + 10}
                  x={scaleX(tick)}
                  textAnchor="middle"
                  fill={theme.darkerText}
                  fontFamily={AHTextFonts.MEDIUM as any}
                  fontSize={7}
                >
                  {DateTime.fromJSDate(tick).toLocaleString({
                    month: "short",
                  })}
                </Text>
              );
            })}

            {scaleY.ticks(5).map((tick, i) => {
              const y = scaleY(tick);
              return (
                <Text
                  key={i + ""}
                  y={innerHeight - y - paddingVertical}
                  x={paddingHorizxontal}
                  fill={theme.darkerText}
                  alignmentBaseline="middle"
                  textAnchor="start"
                  fontFamily={AHTextFonts.MEDIUM as any}
                  fontSize={8}
                >
                  {format(tick) + u}
                </Text>
              );
            })}
          </Svg>
        </Animated.View>
      </GestureDetector>
    </Box>
  );
}

const Header = React.memo(HeaderC);
function HeaderC(props: {
  activeIndex: SharedValue<number>;
  history: readonly {
    date: Date;
    total: number;
    results: { id: string; title: string; val: number; color: string }[];
  }[];
  format: (v: number) => string;
}) {
  const fl = useAnimatedRef<Animated.FlatList<any>>();
  const [activeIndex, setActiveIndex] = useState(props.activeIndex.value ?? 0);
  useAnimatedReaction(
    () => props.activeIndex.value,
    (index, prev) => {
      if (index !== prev) {
        scrollTo(fl, 0, 0, true);

        runOnJS(setActiveIndex)(index);
      }
    }
  );

  if (props.history.length === 0) {
    return null;
  }
  const item = props.history[activeIndex];
  if (!item) {
    return null;
  }

  return (
    <Box>
      <HeaderContainer>
        <Box
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          spaceBottom="small"
        >
          <HeaderItemDetail
            color="black"
            label={
              <FormattedMessage
                description="KPIAnalytics value label avg"
                defaultMessage="Average"
              />
            }
            value={props.format(item.total)}
          />

          <HeaderItemWeekDateTitle date={item.date} />
        </Box>
        <Animated.FlatList
          ref={fl}
          data={item.results.slice().reverse()}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <HeaderSubsection
              key={item.id}
              color={item.color}
              label={item.title}
              value={item.val === 0 ? "0" : props.format(item.val)}
            />
          )}
          horizontal
          showsHorizontalScrollIndicator={false}
        />
      </HeaderContainer>
    </Box>
  );
}

function HeaderContainer(props: PropsWithChildren<unknown>) {
  return (
    <Box spaceHorizontal="medium">
      <Box style={{ overflow: "hidden" }}>{props.children}</Box>
      <AHSeparator />
    </Box>
  );
}

function HeaderItemDetail(props: {
  value: string;
  label: React.ReactNode;
  color: string;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "baseline",
        justifyContent: "flex-start",
        height: 22,
      }}
    >
      <AnimatedCount count={props.value} />
      <AHText type="detail"> {props.label}</AHText>
    </View>
  );
}

function HeaderSubsection(props: {
  value: string;
  label: string;
  color: string;
}) {
  return (
    <Box spaceRight="small" spaceVertical="small">
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          height: 18,
          backgroundColor: props.color,
        }}
        spaceHorizontal="small"
        spaceVertical="xxsmall"
        cornerRadius="small"
      >
        <AHText color="lightText" type="info-small">
          {props.value}
        </AHText>
        <AHText color="lightText" spaceLeft="small" type="detail">
          {props.label}
        </AHText>
      </Box>
    </Box>
  );
}

function AnimatedCount(props: { count: string }) {
  return (
    <AHText
      numberOfLines={1}
      spaceLeft="xsmall"
      style={{
        width: 100,
        textAlignVertical: "top",
        verticalAlign: "top",
        alignItems: "flex-start",
      }}
      type="title"
    >
      {props.count}
    </AHText>
  );
}

function HeaderItemWeekDateTitle(props: { date: Date }) {
  const intl_tokens = useIntlTokens();
  return (
    <AHText type="subtitle" style={{ width: "33%", textAlign: "right" }}>
      {intl_tokens.Week} {DateTime.fromJSDate(props.date).toFormat("W")}
    </AHText>
  );
}
